import type { CreditReason, FreeCancellationMode } from "../enums.ts";
import { evaluate } from "./evaluate.ts";
import type { Policy, PolicyOutcome } from "./types.ts";

/**
 * The ledger reasons that spend one of a package's free cancellations, by mode.
 *
 * The count is read from the ledger, never stored, so this list is the whole definition of "used".
 * It lives next to the engine so the server count and the owner's example can never disagree.
 */
export function allowanceLedgerReasons(mode: FreeCancellationMode): CreditReason[] {
  return mode === "late_only" ? ["late_cancel_waived"] : ["cancel_return", "late_cancel_partial"];
}

/** True when this member cancellation spends one of the package's free cancellations. */
export function spendsFreeCancellation(outcome: PolicyOutcome, mode: FreeCancellationMode): boolean {
  if (mode === "late_only") return outcome.reason === "late_cancel_waived";
  return outcome.creditDelta > 0 && outcome.reason !== "business_cancel";
}

export type CancellationScenario = {
  /** Hours between the cancellation and the session start. */
  hoursBefore: number;
  outcome: PolicyOutcome;
  /** True when the session goes back to the member's balance. */
  sessionReturned: boolean;
  /** True when this cancellation spent one of the package's free cancellations. */
  spent: boolean;
  /** Free cancellations left after this one. Null means the package has no limit. */
  freeLeft: number | null;
};

/**
 * Runs a list of member cancellations on one package through the real engine, in order, and counts
 * the free cancellations the way the server does. The owner's package editor shows the result as
 * a worked example, so what the owner reads is what members get.
 */
export function simulateCancellations(input: {
  policy: Policy;
  freeCancellations: number | null;
  mode: FreeCancellationMode;
  hoursBefore: number[];
}): CancellationScenario[] {
  const starts = new Date("2026-01-15T08:00:00.000Z");
  let used = 0;
  return input.hoursBefore.map((hours) => {
    const outcome = evaluate({
      policy: input.policy,
      sessionStartsAt: starts,
      now: new Date(starts.getTime() - hours * 3_600_000),
      actor: "member",
      bookingSource: "open",
      rescheduleCount: 0,
      monthlyRescheduleCount: 0,
      action: "cancel",
      cancellationAllowance: input.freeCancellations,
      cancellationsUsed: used,
      freeCancellationMode: input.mode,
    });
    const spent = input.freeCancellations !== null && spendsFreeCancellation(outcome, input.mode);
    if (spent) used += 1;
    return {
      hoursBefore: hours,
      outcome,
      sessionReturned: outcome.creditDelta > 0,
      spent,
      freeLeft: input.freeCancellations === null ? null : Math.max(0, input.freeCancellations - used),
    };
  });
}

/**
 * The cancellations the owner's example walks through: two early ones, one late one, and one more
 * early one, which is the case where the two modes give different answers. Hours are relative to
 * the cut-off, so the example stays true for any cut-off the owner picks.
 */
export function exampleCancellationHours(cutoffHours: number): number[] {
  const early = Math.max(cutoffHours + 48, 72);
  const late = cutoffHours > 2 ? Math.min(cutoffHours - 1, Math.max(1, Math.floor(cutoffHours / 4))) : 0;
  return [early, early - 24, late, early];
}
